import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Avatar,
  TextField, InputAdornment, Stack, Grid, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Menu, MenuItem,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  SearchNormal1, Lock, LockSlash, People, Add, Send2, Refresh,
  TickCircle, CloseCircle, Clock, Edit2, Trash, More, Link21,
} from 'iconsax-react';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import { useEmployeeStore } from '../../store/employee.store';
import { Employee, GoogleAccessStatus } from '../../types/employee.type';

type FilterStatus = GoogleAccessStatus | 'all';

function MemberCard({
  emp,
  onSendRequest,
  onReinvite,
  onDisconnect,
  onEdit,
  onDelete,
  resendingId,
}: {
  emp: Employee;
  onSendRequest: (id: string) => void;
  onReinvite: (id: string) => void;
  resendingId: string | null;
  onDisconnect: (id: string) => void;
  onEdit: (emp: Employee) => void;
  onDelete: (emp: Employee) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const statusCfg: Record<GoogleAccessStatus, { bg: string; color: string; border: string; label: string; icon: React.ReactNode }> = {
    pending:  { bg: '#FFFBEB', color: '#D97706', border: '#FCD34D', label: t('accessRequests.statusPending'),  icon: <Clock size={13} color="#D97706" /> },
    approved: { bg: '#F0FDF4', color: '#16A34A', border: '#86EFAC', label: t('accessRequests.statusApproved'), icon: <TickCircle size={13} color="#16A34A" /> },
    rejected: { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA', label: t('accessRequests.statusRejected'), icon: <CloseCircle size={13} color="#DC2626" /> },
    none:     { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB', label: t('accessRequests.statusNone'),     icon: <CloseCircle size={13} color="#6B7280" /> },
  };
  const cfg = statusCfg[emp.google_access_status];

  return (
    <Card sx={{
      width: '100%',
      border: `1.5px solid ${emp.google_access_status === 'approved' ? '#BBF7D0' : '#F3F4F6'}`,
      transition: 'all 0.15s ease',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.07)', transform: 'translateY(-1px)' },
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar sx={{
              width: 42, height: 42, flexShrink: 0,
              background: emp.google_access_status === 'approved'
                ? 'linear-gradient(135deg, #16A34A, #059669)'
                : 'linear-gradient(135deg, #6366F1, #4F46E5)',
              fontWeight: 700, fontSize: '1rem',
            }}>
              {emp.name.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{emp.name}</Typography>
              <Typography noWrap sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{emp.email}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Chip
              icon={cfg.icon as React.ReactElement}
              label={cfg.label}
              size="small"
              sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.75rem', border: `1px solid ${cfg.border}`, height: 24 }}
            />
            <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)}>
              <More size={18} color="#64748B" />
            </IconButton>
          </Box>
        </Box>

        {emp.company && (
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1.5 }}>{emp.company}</Typography>
        )}

        {emp.google_access_status === 'approved' && emp.google_spaces.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.75 }}>{t('accessRequests.groups')}:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {emp.google_spaces.map(s => (
                <Chip key={s.space_id} label={s.space_name} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE' }} />
              ))}
            </Box>
          </Box>
        )}

        {emp.google_access_status === 'approved' && emp.tasks_scope && (
          <Chip
            label={t('accessRequests.scopeSharedSpaces')}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#F1F5F9', color: '#64748B', border: 'none', mb: 1.5 }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1 }}>
          <Typography sx={{ fontSize: '0.73rem', color: 'text.secondary' }}>
            {new Date(emp.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
          {emp.google_access_status === 'none' && (
            <Button
              size="small"
              disabled={resendingId === emp.id}
              startIcon={resendingId === emp.id ? <CircularProgress size={13} color="inherit" /> : <Send2 size={13} color="#2563EB" />}
              onClick={() => onSendRequest(emp.id)}
              sx={{ fontSize: '0.75rem', color: '#2563EB', bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', '&:hover': { bgcolor: '#DBEAFE' } }}
            >
              {t('accessRequests.sendRequest')}
            </Button>
          )}
          {(emp.google_access_status === 'pending' || emp.google_access_status === 'rejected') && (
            <Button
              size="small"
              disabled={resendingId === emp.id}
              startIcon={resendingId === emp.id ? <CircularProgress size={13} color="inherit" /> : <Refresh size={13} color="#D97706" />}
              onClick={() => onReinvite(emp.id)}
              sx={{ fontSize: '0.75rem', color: '#D97706', bgcolor: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '8px', '&:hover': { bgcolor: '#FEF3C7' } }}
            >
              {t('accessRequests.resend')}
            </Button>
          )}
        </Box>
      </CardContent>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); onEdit(emp); }}>
          <Edit2 size={16} color="#64748B" style={{ marginInlineEnd: 8 }} />
          {t('members.editEmployee')}
        </MenuItem>
        {emp.google_access_status === 'approved' && (
          <MenuItem onClick={() => { setMenuAnchor(null); onDisconnect(emp.id); }}>
            <Link21 size={16} color="#64748B" style={{ marginInlineEnd: 8 }} />
            {t('members.disconnectGoogle')}
          </MenuItem>
        )}
        {(emp.google_access_status === 'none' || emp.google_access_status === 'pending' || emp.google_access_status === 'rejected') && (
          <MenuItem onClick={() => { setMenuAnchor(null); onReinvite(emp.id); }}>
            <Send2 size={16} color="#64748B" style={{ marginInlineEnd: 8 }} />
            {t('accessRequests.resend')}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(emp); }} sx={{ color: '#DC2626' }}>
          <Trash size={16} color="#DC2626" style={{ marginInlineEnd: 8 }} />
          {t('members.deleteEmployee')}
        </MenuItem>
      </Menu>
    </Card>
  );
}

export default function MembersPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    employees, isLoading, fetchEmployees, addEmployee, patchEmployeeFields,
    removeEmployee, sendAccessRequest, disconnectGoogle,
  } = useEmployeeStore();

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Employee | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [form, setForm] = useState({ name: '', email: '', company: '' });
  const [editForm, setEditForm] = useState({ name: '', email: '', company: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  useEffect(() => { 
    fetchEmployees(); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => ({
    approved: employees.filter(e => e.google_access_status === 'approved').length,
    pending: employees.filter(e => e.google_access_status === 'pending').length,
    none: employees.filter(e => e.google_access_status === 'none').length,
    rejected: employees.filter(e => e.google_access_status === 'rejected').length,
  }), [employees]);

  const filtered = useMemo(() =>
    employees.filter(e => {
      const matchSearch = !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.email.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || e.google_access_status === filterStatus;
      return matchSearch && matchStatus;
    }),
  [employees, search, filterStatus]);

  const resetForm = () => setForm({ name: '', email: '', company: '' });

  const handleAdd = async () => {
    if (!form.name || !form.email) return;
    setSaving(true);
    try {
      const emp = await addEmployee({ name: form.name, email: form.email, company: form.company || null });
      await sendAccessRequest(emp.id);
      resetForm();
      setAddOpen(false);
      showToast(t('accessRequests.requestSentSuccess', { name: emp.name }), TOAST_TYPES.SUCCESS);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editTarget || !editForm.name || !editForm.email) return;
    setSaving(true);
    try {
      await patchEmployeeFields(editTarget.id, {
        name: editForm.name,
        email: editForm.email,
        company: editForm.company || null,
      });
      setEditTarget(null);
      setEditForm({ name: '', email: '', company: '' });
      showToast(t('members.employeeUpdated'), TOAST_TYPES.SUCCESS);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await removeEmployee(deleteTarget.id);
      showToast(t('members.employeeDeleted'), TOAST_TYPES.SUCCESS);
      setDeleteTarget(null);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setDeleting(false);
    }
  };

  const handleSendRequest = async (id: string) => {
    const name = employees.find(e => e.id === id)?.name ?? '';
    setResendingId(id);
    try {
      await sendAccessRequest(id);
      showToast(t('accessRequests.requestSentSuccess', { name }), TOAST_TYPES.SUCCESS);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setResendingId(null);
    }
  };

  const handleReinvite = async (id: string) => {
    const name = employees.find(e => e.id === id)?.name ?? '';
    setResendingId(id);
    try {
      await sendAccessRequest(id);
      showToast(t('accessRequests.requestResentSuccess', { name }), TOAST_TYPES.SUCCESS);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setResendingId(null);
    }
  };

  const handleDisconnect = async (id: string) => {
    try {
      await disconnectGoogle(id);
      showToast(t('members.googleDisconnected'), TOAST_TYPES.INFO);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    }
  };

  const openEdit = (emp: Employee) => {
    setEditForm({ name: emp.name, email: emp.email, company: emp.company ?? '' });
    setEditTarget(emp);
  };

  const filterButtons: { key: FilterStatus; label: string; activeColor: string }[] = [
    { key: 'all',      label: t('accessRequests.all'),            activeColor: '#2563EB' },
    { key: 'approved', label: t('accessRequests.statusApproved'), activeColor: '#16A34A' },
    { key: 'pending',  label: t('accessRequests.statusPending'),  activeColor: '#D97706' },
    { key: 'rejected', label: t('accessRequests.statusRejected'), activeColor: '#DC2626' },
    { key: 'none',     label: t('accessRequests.statusNone'),     activeColor: '#6B7280' },
  ];

  const EmployeeFormFields = ({ values, onChange }: {
    values: { name: string; email: string; company: string };
    onChange: React.Dispatch<React.SetStateAction<{ name: string; email: string; company: string }>>;
  }) => (
    <Stack spacing={2.5} sx={{ pt: 0.5 }}>
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
          {t('accessRequests.employeeName')} <span style={{ color: '#EF4444' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={values.name}
          onChange={e => onChange(p => ({ ...p, name: e.target.value }))}
          autoFocus
          placeholder={t('accessRequests.employeeName')}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
            },
          }}
        />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
          {t('accessRequests.employeeEmail')} <span style={{ color: '#EF4444' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="email"
          placeholder="employee@gmail.com"
          value={values.email}
          onChange={e => onChange(p => ({ ...p, email: e.target.value }))}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
            },
          }}
        />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
          {t('accessRequests.company')}
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={values.company}
          onChange={e => onChange(p => ({ ...p, company: e.target.value }))}
          placeholder={t('accessRequests.company')}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
            },
          }}
        />
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px' }}>
            {t('members.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
            {t('members.subtitle', { count: employees.length })}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add size={18} color="#fff" />} onClick={() => { resetForm(); setAddOpen(true); }}>
          {t('accessRequests.addEmployee')}
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('members.totalMembers'), value: employees.length, color: '#3B82F6', icon: <People size={18} color="#3B82F6" /> },
          { label: t('members.withAccess'), value: stats.approved, color: '#10B981', icon: <LockSlash size={18} color="#10B981" /> },
          { label: t('accessRequests.statusPending'), value: stats.pending, color: '#D97706', icon: <Clock size={18} color="#D97706" /> },
          { label: t('members.accessNeeded'), value: stats.none + stats.rejected, color: '#F59E0B', icon: <Lock size={18} color="#F59E0B" /> },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>{s.label}</Typography>
                  {s.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('members.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchNormal1 size={16} color="#94A3B8" /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {filterButtons.map(fb => (
            <Button
              key={fb.key}
              size="small"
              onClick={() => setFilterStatus(fb.key)}
              sx={{
                borderRadius: '20px', fontSize: '0.8125rem', px: 1.75,
                bgcolor: filterStatus === fb.key ? fb.activeColor : '#F3F4F6',
                color: filterStatus === fb.key ? '#fff' : 'text.secondary',
                '&:hover': { bgcolor: filterStatus === fb.key ? fb.activeColor : '#E5E7EB' },
              }}
            >
              {fb.label}
            </Button>
          ))}
        </Stack>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <People size={36} color="#D1D5DB" />
                <Typography sx={{ color: 'text.secondary', mt: 1.5, fontSize: '0.875rem' }}>
                  {t('members.noMembersFound')}
                </Typography>
                <Button variant="contained" startIcon={<Add size={16} color="#fff" />} onClick={() => setAddOpen(true)} sx={{ mt: 2 }}>
                  {t('accessRequests.addEmployee')}
                </Button>
              </Box>
            </Grid>
          ) : (
            filtered.map(emp => (
              <Grid item xs={12} sm={6} lg={4} key={emp.id} sx={{ display: 'flex' }}>
                <MemberCard
                  emp={emp}
                  onSendRequest={handleSendRequest}
                  onReinvite={handleReinvite}
                  onDisconnect={handleDisconnect}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                  resendingId={resendingId}
                />
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Add Employee */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #2563EB, #4F46E5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Send2 size={18} color="#fff" />
            </Box>
            {t('accessRequests.addEmployeeTitle')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 2.5 }}>
            {t('accessRequests.addEmployeeDesc')}
          </Typography>
          <EmployeeFormFields values={form} onChange={setForm} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ color: 'text.secondary' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!form.name || !form.email || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Send2 size={16} color="#fff" />}>
            {t('accessRequests.sendRequest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Employee */}
      <Dialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Edit2 size={18} color="#fff" />
            </Box>
            {t('members.editEmployee')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 2.5 }}>
            {t('members.editEmployeeDesc')}
          </Typography>
          <EmployeeFormFields values={editForm} onChange={setEditForm} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setEditTarget(null)} sx={{ color: 'text.secondary' }}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={!editForm.name || !editForm.email || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('members.deleteEmployee')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('members.deleteConfirm', { name: deleteTarget?.name ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: 'text.secondary' }}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : undefined}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
